import React from 'react';
import { Box, Button, CircularProgress } from '@mui/material';
import { FormButton } from './types';

interface CustomButtonsProps {
  buttons: FormButton[];
  disabled?: boolean;
}

export const CustomButtons: React.FC<CustomButtonsProps> = ({
  buttons,
  disabled = false,
}) => {
  const handleButtonClick = (button: FormButton) => {
    if (button.onClick) {
      button.onClick();
    }
  };

  // Group buttons by position
  const leftButtons = buttons.filter(btn => btn.position === 'left' || !btn.position);
  const centerButtons = buttons.filter(btn => btn.position === 'center');
  const rightButtons = buttons.filter(btn => btn.position === 'right');

  const renderButton = (button: FormButton, index: number) => {
    // Auto-determine button type and variant based on position and text
    const isSubmitButton = button.position === 'right' || 
                          button.text.toLowerCase().includes('submit') ||
                          button.text.toLowerCase().includes('save') ||
                          button.text.toLowerCase().includes('next');
    
    const isOutlinedButton = button.position === 'left' || 
                            button.text.toLowerCase().includes('cancel') ||
                            button.text.toLowerCase().includes('back') ||
                            button.text.toLowerCase().includes('draft');
    
    const buttonType = isSubmitButton ? 'submit' : 'button';
    const buttonVariant = isOutlinedButton ? 'outlined' : 'contained';
    const isDisabled = button.disabled || disabled;

    // Handle color prop - convert string to valid MUI color
    const getButtonColor = (color?: string) => {
      if (!color) return 'primary';
      const validColors = ['primary', 'secondary', 'error', 'info', 'success', 'warning'];
      return validColors.includes(color) ? color as any : 'primary';
    };

    return (
      <Button
        key={`${button.text}-${index}`}
        type={buttonType}
        variant={buttonVariant}
        color={getButtonColor(button.color)}
        size={button.size || 'large'}
        disabled={isDisabled}
        onClick={buttonType === 'button' ? () => handleButtonClick(button) : undefined}
        sx={button.sx}
      >
        {button.loading && <CircularProgress size={20} sx={{ mr: 1 }} />}
        {button.text}
      </Button>
    );
  };

  return (
    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      {/* Left buttons */}
      <Box sx={{ display: 'flex', gap: 2 }}>
        {leftButtons.map((button, index) => renderButton(button, index))}
      </Box>

      {/* Center buttons */}
      <Box sx={{ display: 'flex', gap: 2 }}>
        {centerButtons.map((button, index) => renderButton(button, index))}
      </Box>

      {/* Right buttons */}
      <Box sx={{ display: 'flex', gap: 2 }}>
        {rightButtons.map((button, index) => renderButton(button, index))}
      </Box>
    </Box>
  );
}; 